import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import DataMap from 'assets/components/dataMap/DataMap';
import FormatValue from 'assets/components/formatValue/FormatValue';
import NumberInput from 'assets/components/inputs/number/NumberInput';
import InputWrapper from 'assets/components/inputs/wrappers/InputWrapper';
import ThemeTableSimple2 from 'assets/components/table/themes/Theme.Table.Simple.2';
import ToString from 'assets/components/textBox/ToString';
import { getLocales } from 'assets/locales/Locale';
import Theme from 'assets/themes/Theme.Common';
import PricingDistributionTable from 'module/productManagement/components/pricingDistributionTable/PricingDistributionTable';
import { useState } from 'react';
import { Limits, useNonWarrantyPricingProvider } from '../NonWarrantyPricing.Hooks';
import SubfeeDistributionTable from 'module/productManagement/components/pricingDistributionTable/SubfeeDistributionTable';
import Icon from 'assets/components/icon/Icon';
import DropdownButton from 'assets/templates/dropdownButton/DropdownButton';
import ThemeDropdownButtonPopup from 'assets/templates/dropdownButton/themes/Theme.DropdownButton.Popup';
import ThemeButtonMenuItem from 'assets/components/button/themes/Theme.Button.MenuItem';
import SelectInput from 'assets/components/inputs/select/SelectInput';
import { compact, reduce } from 'lodash';
import DistributorDistributionTable from 'module/productManagement/components/pricingDistributionTable/DistributorDistributionTable';
import DealerPricingLimitationTable from 'module/productManagement/components/pricingDistributionTable/DealerPricingLimitationTable';
import DistributionPriceSwitch from 'module/productManagement/components/pricingDistributionTable/DistributionPriceSwitch';
import DealerDistributionDiscountTable from 'module/productManagement/components/pricingDistributionTable/DealerDistributionDiscountTable';
import ThemeOtherVerticalDivider from 'assets/themes/other/Theme.Other.VerticalDivider';
import { addProgressToWindow } from 'assets/components/progressWindow/ProgressWindow';
import { combineStrings } from 'assets/utils/data/String';

export default function NonWarrantyPricingTable() {
  const { lang } = getLocales();
  const {
    getPrice,
    limits,
    terms,
    permissions,
    addEmptyPrices,
    updatePrice,
    updateSubfee,
    data,
    removeTerm,
    removeLimits,
    productApi,
    isAllowedToCopy,
    copy,
    distributorPricingDistributions,
    dealerPricingSettings,
  } = useNonWarrantyPricingProvider();
  const [tempTerm, setTempTerm] = useState<number>();
  const [tempLimits, setTempLimits] = useState<{
    maxVehiclePriceCAD?: number;
    minVehiclePriceCAD?: number;
  }>({});
  const hasTempLimits =
    tempLimits.maxVehiclePriceCAD > 0 &&
    tempLimits.minVehiclePriceCAD > 0 &&
    tempLimits.maxVehiclePriceCAD > tempLimits.minVehiclePriceCAD;

  const disableInputs = !data.length ? !hasTempLimits || !tempTerm : !hasTempLimits && !tempTerm;

  const _addEmptyPrices = () => {
    addEmptyPrices(
      tempTerm ? [tempTerm] : terms,
      hasTempLimits
        ? [
            {
              maxVehiclePriceCAD: tempLimits.maxVehiclePriceCAD,
              minVehiclePriceCAD: tempLimits.minVehiclePriceCAD,
            },
          ]
        : limits
    );
    setTempTerm(null);
    setTempLimits({});
  };
  function updateOriginalPrice({
    termInMonths,
    limit,
    distributionKey,
    distributedValue,
  }: {
    termInMonths: number;
    limit: Limits;
    distributionKey: string;
    distributedValue: Utils.PricingInfo;
  }) {
    const originalDistributions = getPrice(termInMonths, limit)?.pricingDistributions;
    if (originalDistributions) {
      updatePrice(termInMonths, limit, {
        ...originalDistributions,
        [distributionKey]: distributedValue,
      });
    }
  }
  function updateDistributorPrice({
    termInMonths,
    limit,
    distributionKey,
    distributedValue,
  }: {
    termInMonths: number;
    limit: Limits;
    distributionKey: string;
    distributedValue: Utils.PricingInfo;
  }) {
    const currentPricePoint = getPrice(termInMonths, limit);
    if (currentPricePoint?.pricingDistributions) {
      const currentDistribution = distributorPricingDistributions.getDistributorPrice(
        currentPricePoint?.id,
        'NonWarrantyPricePoint'
      )?.distributions;
      distributorPricingDistributions.updateDistributorPrice({
        pricePointId: currentPricePoint.id,
        pricePointType: 'NonWarrantyPricePoint',
        distributions: {
          ...currentDistribution,
          [distributionKey]: distributedValue,
        },
      });
    }
  }
  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          marginBottom: Theme.Size.M,
          gap: Theme.Size.M,
          justifyContent: 'flex-end',
        }}
      >
        {isAllowedToCopy && (
          <Button
            class={ThemeButtonCircle}
            media="fas-copy"
            onClick={copy}
            render={lang.copyDefaultPricesHere}
            style={{ marginRight: 'auto' }}
            htmlElementProps={{ title: lang.copy }}
          />
        )}
        {distributorPricingDistributions.canView && (
          <InputWrapper>
            {!!distributorPricingDistributions.distributors?.length &&
              !distributorPricingDistributions.isDealerOrDistributor && (
                <SelectInput
                  name="distributorId"
                  value={distributorPricingDistributions.distributorId}
                  label={lang.distributors}
                  data={distributorPricingDistributions.distributors?.map((item) => ({
                    ...item.displayInfo,
                    disabled:
                      !!dealerPricingSettings.dealerId && item.id !== dealerPricingSettings.dealer?.representativeId,
                  }))}
                  onChange={({ distributorId }) => distributorPricingDistributions.setDistributorId(distributorId)}
                  style={{ width: '200px' }}
                />
              )}
            <SelectInput
              name="templateId"
              value={distributorPricingDistributions.distributorData?.templateId}
              label={lang.pricingDistributionTemplate}
              data={distributorPricingDistributions.templates?.map((item) => item.displayInfo)}
              onChange={({ templateId }) =>
                distributorPricingDistributions.setDistributorData({
                  templateId,
                  isUpdated: true,
                  distributions: [],
                  template: distributorPricingDistributions.templates?.find((it) => it.id === templateId),
                })
              }
              disabled={!!distributorPricingDistributions.productRelation?.pricingDistributionTemplateId}
              style={{ width: '200px' }}
            />
          </InputWrapper>
        )}
        {dealerPricingSettings.canView && (
          <InputWrapper>
            {!!dealerPricingSettings.dealers?.length && (
              <SelectInput
                name="dealerId"
                value={dealerPricingSettings.dealerId}
                label={lang.dealers}
                data={dealerPricingSettings.dealers?.map((item) => ({
                  ...item.displayInfo,
                  actionIcon: <Icon class="fas-copy" />,
                  actionTitle: 'Copy adjustments to this dealer',
                  disableEdit:
                    !dealerPricingSettings.dealerId ||
                    dealerPricingSettings.dealerId === item.id ||
                    !distributorPricingDistributions.distributorId ||
                    item.representativeId !== distributorPricingDistributions.distributorId,
                  disabled:
                    !!distributorPricingDistributions.distributorId &&
                    item.representativeId !== distributorPricingDistributions.distributorId,
                }))}
                onChange={({ dealerId }) => {
                  dealerPricingSettings.setDealerId(dealerId);
                  const distributorId = dealerPricingSettings.dealers?.find(
                    (it) => it.id === dealerId
                  )?.representativeId;
                  if (distributorId) distributorPricingDistributions.setDistributorId(distributorId);
                }}
                style={{ width: '200px' }}
                onEdit={(id) => dealerPricingSettings.copyTo(id, addProgressToWindow)}
              />
            )}
          </InputWrapper>
        )}
        {distributorPricingDistributions.canEdit && !distributorPricingDistributions.isDealerOrDistributor && (
          <InputWrapper>
            <NumberInput
              label={`${lang.add}: ${lang.term}`}
              name="term"
              value={tempTerm}
              onChange={({ term }) => setTempTerm(term)}
              style={{ formInput: { maxWidth: '200px' } }}
              step={1}
              min={0}
              roundOnBlur
            />
            <NumberInput
              label={`${lang.add}: ${lang.minPrice}`}
              name="minVehiclePriceCAD"
              value={tempLimits.minVehiclePriceCAD}
              onChange={(d) => setTempLimits((old) => ({ ...old, ...d }))}
              style={{ formInput: { maxWidth: '200px' } }}
              step={1}
              min={0}
              roundOnBlur
            />
            <NumberInput
              label={`${lang.add}: ${lang.maxPrice}`}
              name="maxVehiclePriceCAD"
              value={tempLimits.maxVehiclePriceCAD}
              onChange={(d) => setTempLimits((old) => ({ ...old, ...d }))}
              step={1}
              min={1}
              roundOnBlur
              style={{ formInput: { maxWidth: '200px' } }}
            />
            <Button
              class={ThemeButtonCircle}
              media="fas-plus"
              disabled={disableInputs}
              onClick={_addEmptyPrices}
              style={{ border: `${Theme.Size.XXS} solid ${Theme.Color.shadowDark}` }}
            />
          </InputWrapper>
        )}
      </div>
      <table className={ThemeTableSimple2}>
        <thead>
          <tr>
            <th style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{lang.price}</th>
            <DataMap
              data={terms}
              render={({ data: termInMonths }) => (
                <th key={termInMonths} style={{ textAlign: 'right', position: 'relative' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      {dealerPricingSettings.canView && !!dealerPricingSettings.dealer && (
                        <>
                          <DealerPricingLimitationTable
                            isEditDisabled={dealerPricingSettings.canEdit}
                            columnKey={`DealerLimitations-${termInMonths.toString()}`}
                            rows={limits}
                            getDiscountPrices={(limit) => {
                              const pricePoint = getPrice(termInMonths, limit);
                              return {
                                base: dealerPricingSettings.getDiscountPrice(
                                  pricePoint?.id,
                                  'NonWarrantyPricePoint',
                                  pricePoint?.pricingDistributions
                                ),
                                distributor: dealerPricingSettings.getDistributorDiscountPrice(
                                  pricePoint?.id,
                                  'NonWarrantyPricePoint',
                                  distributorPricingDistributions.getDistributorPrice(
                                    pricePoint?.id,
                                    'NonWarrantyPricePoint'
                                  )?.distributions
                                ),
                              };
                            }}
                            getLimitation={(limit) =>
                              dealerPricingSettings.getDistributionLimitation(
                                getPrice(termInMonths, limit)?.id,
                                'NonWarrantyPricePoint'
                              )?.limitations
                            }
                            updateLimitation={(limit, limitations) =>
                              dealerPricingSettings.updateDistributionLimitation({
                                limitations,
                                pricePointId: getPrice(termInMonths, limit)?.id,
                                pricePointType: 'NonWarrantyPricePoint',
                              })
                            }
                            priceOption={({ delta, property, unit }) => (
                              <DropdownButton
                                buttonMedia="fas-copy"
                                horizontalPosition="auto_fixed"
                                verticalPosition="auto_fixed"
                                tabIndex={null}
                                subcomponents={ThemeDropdownButtonPopup}
                                htmlElementProps={{ title: lang.copy }}
                                title={lang.increaseAll}
                                disabled={unit !== 'replacement' || delta < 0}
                                dropdownOptions={({ animateOut }) => (
                                  <Button
                                    htmlElementProps={{ title: '' }}
                                    class={ThemeButtonMenuItem}
                                    onClick={() => {
                                      animateOut();
                                      dealerPricingSettings.updateDistributionLimitation(
                                        compact(
                                          data.map((pricePoint) => {
                                            const currentLimitations = dealerPricingSettings.getDistributionLimitation(
                                              pricePoint?.id,
                                              'NonWarrantyPricePoint'
                                            );
                                            if (
                                              !currentLimitations?.limitations?.unit ||
                                              currentLimitations?.limitations.unit === unit
                                            ) {
                                              const currentBasePrice =
                                                dealerPricingSettings.getDiscountPrice(
                                                  pricePoint?.id,
                                                  'NonWarrantyPricePoint',
                                                  pricePoint.pricingDistributions
                                                ) +
                                                reduce(
                                                  distributorPricingDistributions.getDistributorPrice(
                                                    pricePoint?.id,
                                                    'NonWarrantyPricePoint'
                                                  )?.distributions || {},
                                                  (prev, curr) => prev + curr.amount,
                                                  0
                                                );
                                              return {
                                                ...currentLimitations,
                                                limitations: {
                                                  ...currentLimitations?.limitations,
                                                  unit,
                                                  [property]: delta === 0 ? null : currentBasePrice + delta,
                                                },
                                                pricePointId: pricePoint?.id,
                                                pricePointType: 'NonWarrantyPricePoint',
                                              };
                                            }
                                            return null;
                                          })
                                        )
                                      );
                                    }}
                                  >
                                    <span>
                                      {property === 'minAmount' ? 'Sug. retail' : 'Max'} prices by{' '}
                                      <FormatValue value={`${delta.toFixed(2)}$`} />
                                    </span>
                                  </Button>
                                )}
                              />
                            )}
                          />
                          <DealerDistributionDiscountTable
                            isEditDisabled={dealerPricingSettings.canEdit}
                            columnKey={`DealerDistributionDiscount-${termInMonths.toString()}`}
                            rows={limits}
                            columns={productApi?.payload?.templateKeys}
                            getDiscount={(limit) =>
                              dealerPricingSettings.getDistributionDiscount(
                                getPrice(termInMonths, limit)?.id,
                                'NonWarrantyPricePoint'
                              )?.distributionDiscount
                            }
                            updateDiscount={(limit, distribution) => {
                              if (productApi.payload.id && dealerPricingSettings.dealerId) {
                                const pricePoint = getPrice(termInMonths, limit);
                                const dealerSettings = dealerPricingSettings.getDistributionDiscount(
                                  pricePoint?.id,
                                  'NonWarrantyPricePoint'
                                );
                                dealerPricingSettings.updateDistributionDiscount({
                                  ...dealerSettings,
                                  productId: productApi.payload.id,
                                  dealerId: dealerPricingSettings.dealerId,
                                  pricePointId: pricePoint?.id,
                                  pricePointType: 'NonWarrantyPricePoint',
                                  distributionDiscount: {
                                    ...dealerSettings?.distributionDiscount,
                                    ...distribution,
                                  },
                                });
                              }
                            }}
                          />
                          <div className={ThemeOtherVerticalDivider} />
                        </>
                      )}
                      {distributorPricingDistributions.canView && !!distributorPricingDistributions.distributor && (
                        <>
                          <DistributorDistributionTable
                            isEditDisabled={distributorPricingDistributions.canEdit}
                            columnKey={`Distributor-${termInMonths.toString()}`}
                            rows={limits}
                            columns={distributorPricingDistributions.distributorData?.template?.keys}
                            getPrice={(limit) =>
                              distributorPricingDistributions.getDistributorPrice(
                                getPrice(termInMonths, limit)?.id,
                                'NonWarrantyPricePoint'
                              )?.distributions
                            }
                            updatePrice={(limit, distributions) =>
                              distributorPricingDistributions.updateDistributorPrice({
                                distributions,
                                pricePointId: getPrice(termInMonths, limit)?.id,
                                pricePointType: 'NonWarrantyPricePoint',
                              })
                            }
                            priceOption={({ distributedValue, distributionKey }) => (
                              <DropdownButton
                                buttonMedia="fas-copy"
                                horizontalPosition="auto_fixed"
                                verticalPosition="auto_fixed"
                                tabIndex={null}
                                subcomponents={ThemeDropdownButtonPopup}
                                htmlElementProps={{ title: lang.copy }}
                                title={lang.copyTo}
                                dropdownOptions={({ animateOut }) => (
                                  <>
                                    <Button
                                      htmlElementProps={{ title: '' }}
                                      class={ThemeButtonMenuItem}
                                      onClick={() => {
                                        animateOut();
                                        for (const limit of limits) {
                                          updateDistributorPrice({
                                            limit,
                                            termInMonths,
                                            distributedValue,
                                            distributionKey,
                                          });
                                        }
                                      }}
                                    >
                                      {lang.thisColumn}
                                    </Button>
                                  </>
                                )}
                              />
                            )}
                          />
                          {dealerPricingSettings.canView && !!dealerPricingSettings.dealer && (
                            <DealerDistributionDiscountTable
                              isEditDisabled={dealerPricingSettings.canEdit}
                              isDistributorDiscount
                              columnKey={`DealerDistributorDistributionDiscount-${termInMonths.toString()}`}
                              rows={limits}
                              columns={distributorPricingDistributions.distributorData?.template?.keys}
                              getDiscount={(limit) =>
                                dealerPricingSettings.getDistributionDiscount(
                                  getPrice(termInMonths, limit)?.id,
                                  'NonWarrantyPricePoint'
                                )?.distributorDistributionDiscount
                              }
                              updateDiscount={(limit, distribution) => {
                                const pricePoint = getPrice(termInMonths, limit);
                                if (productApi.payload.id && dealerPricingSettings.dealerId) {
                                  const dealerSettings = dealerPricingSettings.getDistributionDiscount(
                                    pricePoint?.id,
                                    'NonWarrantyPricePoint'
                                  );
                                  dealerPricingSettings.updateDistributionDiscount({
                                    ...dealerSettings,
                                    productId: productApi.payload.id,
                                    dealerId: dealerPricingSettings.dealerId,
                                    pricePointId: pricePoint?.id,
                                    pricePointType: 'NonWarrantyPricePoint',
                                    distributorDistributionDiscount: {
                                      ...dealerSettings?.distributorDistributionDiscount,
                                      ...distribution,
                                    },
                                  });
                                }
                              }}
                            />
                          )}
                          <div className={ThemeOtherVerticalDivider} />
                        </>
                      )}

                      {!distributorPricingDistributions.isDealerOrDistributor && (
                        <>
                          <PricingDistributionTable
                            isEditDisabled={distributorPricingDistributions.canEdit}
                            columnKey={`PricingDistribution-${termInMonths.toString()}`}
                            rows={limits}
                            columns={productApi?.payload?.templateKeys}
                            getPrice={(limit) => getPrice(termInMonths, limit)?.pricingDistributions}
                            updatePrice={(limit, distribution) => updatePrice(termInMonths, limit, distribution)}
                            priceOption={({ distributedValue, distributionKey }) => (
                              <DropdownButton
                                buttonMedia="fas-copy"
                                horizontalPosition="auto_fixed"
                                verticalPosition="auto_fixed"
                                tabIndex={null}
                                subcomponents={ThemeDropdownButtonPopup}
                                htmlElementProps={{ title: lang.copy }}
                                title={lang.copyTo}
                                dropdownOptions={({ animateOut }) => (
                                  <>
                                    <Button
                                      class={ThemeButtonMenuItem}
                                      onClick={() => {
                                        animateOut();
                                        for (const limit of limits) {
                                          updateOriginalPrice({
                                            limit,
                                            distributedValue,
                                            distributionKey,
                                            termInMonths,
                                          });
                                        }
                                      }}
                                    >
                                      {lang.thisColumn}
                                    </Button>
                                  </>
                                )}
                              />
                            )}
                          />
                          <SubfeeDistributionTable
                            isEditDisabled={distributorPricingDistributions.canEdit}
                            columnKey={`Subfees-${termInMonths.toString()}`}
                            rows={limits}
                            columns={productApi?.payload?.subfee}
                            getPrice={(limit) => getPrice(termInMonths, limit)?.subfees}
                            updatePrice={(limit, subfees) => updateSubfee(termInMonths, limit, subfees)}
                          />
                          <Button
                            class={ThemeButtonCircle}
                            style={{ button: { display: 'inline-block' } }}
                            media="fas-trash"
                            onClick={removeTerm}
                            data={termInMonths}
                            disabled={data.some((d) => d.termInMonths === termInMonths && d.id)}
                            htmlElementProps={{ title: lang.remove }}
                          />
                        </>
                      )}
                    </div>
                    <ToString value={`${termInMonths}m`} />
                  </div>
                </th>
              )}
            />
          </tr>
        </thead>
        <tbody>
          <DataMap
            data={limits}
            render={({ data: limit }) => (
              <tr key={limit.minVehiclePriceCAD + ' ' + limit.maxVehiclePriceCAD}>
                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    {!distributorPricingDistributions.isDealerOrDistributor && (
                      <Button
                        class={ThemeButtonCircle}
                        style={{ button: { display: 'inline-block' } }}
                        media="fas-trash"
                        onClick={removeLimits}
                        data={limit}
                        disabled={data.some(
                          (d) =>
                            d.minVehiclePriceCAD === limit.minVehiclePriceCAD &&
                            d.maxVehiclePriceCAD === limit.maxVehiclePriceCAD &&
                            d.id
                        )}
                        htmlElementProps={{ title: `${lang.remove} ${lang.limit}` }}
                      />
                    )}
                    <span>
                      <FormatValue value={`${limit.minVehiclePriceCAD.toFixed(2)}$`} />
                      &nbsp;-&nbsp;
                      <FormatValue value={`${limit.maxVehiclePriceCAD.toFixed(2)}$`} />
                    </span>
                  </div>
                </td>
                <DataMap
                  data={terms}
                  render={({ data: termInMonths }) => {
                    const pricePoint = getPrice(termInMonths, limit);
                    const isUnset = pricePoint?.priceCAD == null;
                    const distributorDistribution = distributorPricingDistributions.getDistributorPrice(
                      pricePoint?.id,
                      'NonWarrantyPricePoint'
                    );
                    const distributorPriceSegment = reduce(
                      distributorDistribution?.distributions || {},
                      (prev, curr) => prev + curr.amount,
                      0
                    );
                    const basePrice = pricePoint?.priceCAD ?? 0;
                    const distributorPrice = basePrice + distributorPriceSegment;
                    const basePriceWithDiscount = dealerPricingSettings.getDiscountPrice(
                      pricePoint?.id,
                      'NonWarrantyPricePoint',
                      pricePoint?.pricingDistributions
                    );
                    const distributorPriceWithDiscount = dealerPricingSettings.getDistributorDiscountPrice(
                      pricePoint?.id,
                      'NonWarrantyPricePoint',
                      distributorDistribution?.distributions
                    );
                    const finalPrice = basePriceWithDiscount + distributorPriceWithDiscount;

                    const showDealerPrice = distributorPrice > 0 && !!dealerPricingSettings.dealerId;
                    const showDistributorPrice =
                      distributorPrice > 0 && !!distributorPricingDistributions.distributorId;
                    return (
                      <td style={{ textAlign: 'right' }} key={termInMonths}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div style={{ display: 'flex' }}>
                            {distributorPricingDistributions.canView &&
                              !!distributorPricingDistributions.distributorData?.template?.keys && (
                                <Button
                                  disabled={!distributorDistribution || permissions.DELETE_NONWARRANTYPRICEPOINT}
                                  onClick={() =>
                                    distributorPricingDistributions.updateDistributorPrice({
                                      pricePointId: pricePoint?.id,
                                      distributions: {},
                                      pricePointType: 'NonWarrantyPricePoint',
                                    })
                                  }
                                  class={{ button: ThemeButtonCircle }}
                                  media={<Icon class="fas-user-slash" />}
                                  htmlElementProps={{ title: lang.clearDistributorPrice }}
                                />
                              )}
                            {!distributorPricingDistributions.isDealerOrDistributor && (
                              <Button
                                disabled={isUnset || permissions.DELETE_NONWARRANTYPRICEPOINT}
                                onClick={() => updatePrice(termInMonths, limit, null)}
                                class={{ button: ThemeButtonCircle }}
                                media={<Icon class="fas-trash-alt" />}
                                htmlElementProps={{ title: `${lang.clear} ${lang.price}` }}
                              />
                            )}
                          </div>
                          {showDealerPrice || showDistributorPrice ? (
                            <span
                              style={{
                                fontWeight: isUnset ? undefined : 'bold',
                                color: isUnset ? Theme.Color.warning : undefined,
                              }}
                            >
                              <span style={{ fontSize: Theme.FontSize.S }}>
                                {combineStrings(
                                  '/',
                                  showDealerPrice ? lang.dealer : undefined,
                                  showDistributorPrice ? lang.distributor : undefined,
                                  lang.base
                                )}
                              </span>
                              <br />
                              <span style={{ fontSize: Theme.FontSize.S }}>
                                {showDealerPrice && (
                                  <>
                                    <FormatValue value={isUnset ? 'N/A' : `${finalPrice.toFixed(2)}$`} />/
                                  </>
                                )}
                                {showDistributorPrice && (
                                  <>
                                    <FormatValue value={isUnset ? 'N/A' : `${distributorPrice.toFixed(2)}$`} />/
                                  </>
                                )}
                                <FormatValue value={isUnset ? 'N/A' : `${basePrice.toFixed(2)}$`} />
                              </span>
                            </span>
                          ) : (
                            <span
                              style={{
                                fontWeight: isUnset ? undefined : 'bold',
                                color: isUnset ? Theme.Color.warning : undefined,
                              }}
                            >
                              <FormatValue value={isUnset ? 'N/A' : `${basePrice.toFixed(2)}$`} />
                            </span>
                          )}
                        </div>
                      </td>
                    );
                  }}
                />
              </tr>
            )}
          />
        </tbody>
      </table>
      <DistributionPriceSwitch
        templateKeys={productApi?.payload?.templateKeys ?? []}
        onUpdate={(firstKey, secondKey) => {
          for (const termInMonths of terms) {
            for (const limit of limits) {
              const originalDistributions = getPrice(termInMonths, limit)?.pricingDistributions;
              if (originalDistributions) {
                updatePrice(termInMonths, limit, {
                  ...originalDistributions,
                  [firstKey]: originalDistributions[secondKey],
                  [secondKey]: originalDistributions[firstKey],
                });
              }
            }
          }
        }}
      />
    </>
  );
}
